import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PartnershipType } from "../../types/genericType";
import {
  Paper,
  Typography,
  Stack,
  Box,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import "./styles.scss";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridSortModel,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import NewButton from "../common/NewButton/NewButton";
import SummaryModal from "./SummaryModal";
import { useDispatch, useSelector } from "react-redux";
import { Delete, FileDownload, Visibility } from "@mui/icons-material";
import { removeDistribution } from "../../redux/features/partnershipsSlice";
import { deleteDistribution } from "../../services/partnershipService";
import dayjs from 'dayjs';
import { formatDollar } from "../../utils/utils";
import useFileDownloader from "../../hooks/useFileDownloader";

type DistributionDetailsProps = {
  selectedPartnership: PartnershipType | null;
};

const DistributionDetails = (props: DistributionDetailsProps) => {
  const { selectedPartnership } = props;
  const navigate = useNavigate();
  const { distributions } = useSelector(
    (state: any) => state.partnershipsState
  );
  const dispatch = useDispatch();
  const { downloadFile } = useFileDownloader();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<number | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "date", sort: "desc" },
  ]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };
  const handleCloseConfirm = () => setConfirm(null);
  const handleClose = () => setOpen(false);

  const handleViewTransactions = (distributionId: number) => {
    navigate(`/transactions/transactionHistory?distributionId=${distributionId}`);
  };

  const handleDownload = async (distributionId: number) => {
    console.log("Download clicked for distribution ID:", distributionId);
    try {
      console.log("Attempting to download PDF...");
      const result = await downloadFile({
        url: `/reports/distribution-transactions/${distributionId}`,
        method: "get",
        fileName: `distribution-${distributionId}.pdf`,
      });
      
      console.log("Download result:", result);
      
      if (!result.success) {
        console.error("Error downloading distribution PDF - result.success is false");
      } else {
        console.log("Download successful!");
      }
    } catch (error) {
      console.error("Error downloading distribution PDF:", error);
    }
  };

  const onConfirm = () => {
    if (confirm) {
      deleteDistribution(confirm)
        .then(() => {
          dispatch(removeDistribution(confirm));
        })
        .catch((err) => {
          console.log(err);
        });

      handleCloseConfirm();
    }
  };

  const columns: GridColDef[] = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "date",
        headerName: "Date",
        align: "left",
        headerAlign: "left",
        hideable: false,
        flex: 1,
        minWidth: 120,
        maxWidth: 200,
        headerClassName: "header-datagrid",
        valueFormatter: (params: GridValueFormatterParams) =>
          dayjs(params.value).format("MM/DD/YYYY"),
      },
      {
        field: "amount",
        headerName: "Amount",
        align: "right",
        headerAlign: "right",
        hideable: false,
        flex: 1,
        minWidth: 120,
        maxWidth: 180,
        sortable: false,
        type: "number",
        sortComparator: (a: string, b: string) =>
          Number(a.slice(1)) - Number(b.slice(1)),
        valueGetter: (params: GridValueGetterParams) =>
          `${formatDollar(params.row.amount) || ""}`,
        headerClassName: "header-datagrid",
      },
      {
        field: "id",
        headerName: "",
        sortable: false,
        width: 120,
        align: "left",
        headerAlign: "center",
        headerClassName: "header-datagrid",
        type: "actions",
        getActions: (params: GridValueGetterParams) => {
          const currentMonth = dayjs().month();
          const currentYear = dayjs().year();
          const creationYear = dayjs(params?.row?.date).year();
          const creationMonth = dayjs(params?.row?.date).month();
          
          const actions = [
            <GridActionsCellItem
              key={`view-${params.row.id}`}
              icon={<Visibility sx={{ fontSize: "20px" }} />}
              label="View"
              onClick={() => handleViewTransactions(params.row.id)}
            />,
            <GridActionsCellItem
              key={`download-${params.row.id}`}
              icon={<FileDownload sx={{ fontSize: "20px" }} />}
              label="Download"
              onClick={() => {
                console.log("Download icon clicked, calling handleDownload with ID:", params.row.id);
                handleDownload(params.row.id);
              }}
            />,
          ];

          if (currentMonth === creationMonth && currentYear === creationYear) {
            actions.push(
              <GridActionsCellItem
                key={`delete-${params.row.id}`}
                icon={<Delete sx={{ fontSize: "20px" }} />}
                label="Delete"
                onClick={() => setConfirm(params.row.id)}
              />
            );
          }
          
          return actions;
        },
      },
    ],
    []
  );

  const now = dayjs();
  const alreadyRunCurrentMonth = distributions.find(
    (distribution: any) =>
      now.month() === dayjs(distribution.date).month() &&
      now.year() === dayjs(distribution.date).year()
  );
  return (
    <Paper elevation={3} className="container">
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack spacing={1}>
            <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>
              {selectedPartnership?.name}
            </Typography>
            Distribution History
          </Stack>
          <Box sx={{ height: "36px" }}>
            <NewButton
              handleBtnClick={() => {
                setOpen(true);
              }}
              title="New Distribution"
              type="contained"
              sx={{ height: "36px", width: "246px" }}
              disabled={alreadyRunCurrentMonth}
            />
          </Box>
        </Stack>
        <DataGrid
          rows={distributions}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={[10, 20, 30, 40, 50]}
          rowSelection={false}
          className="distribution-grid"
        />
      </Stack>
      {selectedPartnership && open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <SummaryModal
            partnershipId={selectedPartnership.id}
            handleClose={handleClose}
            selectedPartnership={selectedPartnership}
          />
        </Modal>
      )}
      {confirm && (
        <Dialog
          open={true}
          onClose={handleCloseConfirm}
          aria-labelledby="draggable-dialog-title"
        >
          <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
            Delete this distribution?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              You will not be able to undo this action.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" autoFocus onClick={handleCloseConfirm}>
              Cancel
            </Button>
            <Button variant="contained" onClick={onConfirm}>
              Delete Distribution
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
};

export default DistributionDetails;
